import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

// UX
import CardOperatingOrderInterfaceUX from "./CardOperatingOrderInterfaceUX";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Utils
import { Checkbox } from "semantic-ui-react";

// Interface
import { useIntl } from "react-intl";
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  SaveType,
  FilterSearchType,
  WidthsType,
  GenerateTableHeader,
  PaymentStatusOptions,
  TypeOptions,
} from "./sequence/OperatingOrderInterface";

// Serializer
import OperatingOrderInterfaceSerializer from "issara-sdk/types/OperatingOrderSerializer_apps_ORM";
import EncounterSerializer from "issara-sdk/types/EncounterSerializer_core";

// Type
type CardOperatingOrderInterfaceProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  OperatingOrderInterfaceSequence: State["OperatingOrderInterfaceSequence"];
  languageUX: "en" | "th";
} & PickedProps;

const CardOperatingOrderInterface = (props: CardOperatingOrderInterfaceProps) => {
  const intl = useIntl();

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);
  const [selectedOperating, setSelectedOperating] = useState<any[]>([]);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "OperatingOrderInterface", restart: true });
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data ?? ""}</div>;
  };

  const handleCheckAll = (_e: any, v: any) => {
    if (v.checked) {
      let newData = (props.OperatingOrderInterfaceSequence?.OperatingOrderInterfaceList || []).map(
        (item: OperatingOrderInterfaceSerializer) => item.id
      );

      setSelectedOperating(newData);
    } else {
      setSelectedOperating([]);
    }
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "OperatingOrderInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      page: 0,
    });

    setSelectedOperating([]);
  };

  const handleSave = (type: SaveType) => {
    props.runSequence({
      sequence: "OperatingOrderInterface",
      action: "SAVE",
      selectedItems: selectedOperating,
      cardKey: CARD_NAME,
      type,
    });

    setSelectedOperating([]);
  };

  const handleClear = () => {
    props.runSequence({
      sequence: "OperatingOrderInterface",
      action: "CLEAR",
      cardKey: CARD_NAME,
    });

    setSelectedOperating([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof FilterSearchType;
    props.setProp(`OperatingOrderInterfaceSequence.searchOperatingOrder.${name}`, data?.value);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`OperatingOrderInterfaceSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "OperatingOrderInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      [key]: value,
    });

    setSelectedOperating([]);
    props.setProp(`OperatingOrderInterfaceSequence.selectedItems`, []);
  };

  const handleDisabledTrigger = useMemo(() => {
    if (selectedOperating.length === 0) return true;
  }, [selectedOperating, props.OperatingOrderInterfaceSequence?.OperatingOrderInterfaceList]);

  const formatEmr = useCallback((encounterData: EncounterSerializer | undefined, emrId: number | undefined | null) => {
    const id = encounterData?.id ? encounterData.id : "-";
    const number = encounterData?.number ? encounterData?.number : "-";
    const patient = encounterData?.patient_name;

    return `[EMR ${emrId}] [${id} : ${number}] ${patient}`;
  }, [props.OperatingOrderInterfaceSequence?.encounterList]);

  // useMemo
  const operatingOrderInterfaceList = useMemo(() => {
    return (props.OperatingOrderInterfaceSequence?.OperatingOrderInterfaceList || []).map(
      (item: OperatingOrderInterfaceSerializer) => {
        // let editedDoctor = (props.OperatingOrderInterfaceSequence?.userOptions || []).find(
        //   (data: OperatingOrderInterfaceSerializer) => data?.id === item.edited_doctor
        // );

        // let orderedName = (props.OperatingOrderInterfaceSequence?.userOptions || []).find(
        //   (data: OperatingOrderInterfaceSerializer) => data?.id === item.order_by
        // );

        // let receivedName = (props.OperatingOrderInterfaceSequence?.userOptions || []).find(
        //   (data: OperatingOrderInterfaceSerializer) => data?.id === item.order_receive_by
        // );

        const paymentStatus = (PaymentStatusOptions || []).find(
          (data: any) => data.value === item.order_payment_status
        );

        const typeStatus = (TypeOptions || []).find(
          (data: any) => data.value === item.type
        );

        const encounterData = (props.OperatingOrderInterfaceSequence?.encounterList || []).find(
          (data: EncounterSerializer) => data?.id === item.encounter
        );

        return {
          ...item,
          _checked: columnTableCenter(
            <Checkbox
              checked={selectedOperating.includes(item.id)}
              // disabled={}
              onChange={(e: any, v: any) => {
                if (v.checked) {
                  setSelectedOperating([...selectedOperating, item.id]);
                } else {
                  let newData = selectedOperating.filter((key: any) => key !== item.id);
                  setSelectedOperating(newData);
                }
              }}
            />,
            { padding: "10px" }
          ),
          ...tableHeaders.reduce(
            (data: Partial<Record<keyof OperatingOrderInterfaceSerializer, ReactNode>>, header) => {
              const key = header.accessor;

              if (dateTimeColumns.includes(key)) {
                data[key] = columnTableCenter(item[key] ? dateToStringBE(item[key]) : item[key]);
              } else if (conditionColumns.includes(key)) {
                data[key] = columnTableCenter(item[key] ? "Yes" : "No");
              } else if (key === "order_payment_status") {
                data[key] = columnTableCenter(paymentStatus?.text);
              } else if (key === "emr") {
                data[key] = columnTableCenter(
                  formatEmr(encounterData, item[key])
                );
              } else if (key === "type") {
                data[key] = columnTableCenter(typeStatus?.text);
              } else if (leftColumns.includes(key)) {
                data[key] = item[key];
              } else {
                data[key] = columnTableCenter(item[key]);
              }

              return data;
            },
            {}
          ),
        };
      }
    );
  }, [
    selectedOperating,
    props.OperatingOrderInterfaceSequence?.OperatingOrderInterfaceList,
    // props.OperatingOrderInterfaceSequence?.userOptions,
    tableHeaders,
    PaymentStatusOptions,
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardOperatingOrderInterfaceUX
        onChangeData={handleChangeData}
        onClearFilter={handleClear}
        searchOperatingOrder={props.OperatingOrderInterfaceSequence?.searchOperatingOrder}
        // table
        tableHeaders={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedOperating.length ===
                    (props.OperatingOrderInterfaceSequence?.OperatingOrderInterfaceList || [])
                      .length &&
                  (props.OperatingOrderInterfaceSequence?.OperatingOrderInterfaceList || [])
                    .length > 0
                }
                onChange={handleCheckAll}
              />
            ),
            accessor: "_checked",
            width: 60,
          },
          ...tableHeaders,
        ]}
        operatingOrderInterfaceList={operatingOrderInterfaceList}
        paginatedItems={props.OperatingOrderInterfaceSequence?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        searchButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={`${CARD_NAME}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SEARCH`]}
            // config
            color="blue"
            title={intl.formatMessage({ id: "ค้นหา" })}
            name="SEARCH"
            size="medium"
          />
        }
        triggerButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("TRIGGER")}
            disabled={handleDisabledTrigger}
            // data
            paramKey={`${CARD_NAME}_TRIGGER`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_TRIGGER`]}
            // config
            color="blue"
            title="Trigger"
            name="TRIGGER"
            size="medium"
          />
        }
      />
    </>
  );
};

CardOperatingOrderInterface.displayName = "CardOperatingOrderInterface";

export default React.memo(CardOperatingOrderInterface);

const conditionColumns: Partial<keyof OperatingOrderInterfaceSerializer>[] = [
  "is_advance",
  "is_anesthesia",
  "is_appointment",
  "is_checkin",
  "is_encounter_expired",
  "is_ipd_patient",
  "is_med_block",
  "is_other",
  "is_out_perform_div",
  "is_paid",
  "is_seen",
];

const leftColumns: Partial<keyof OperatingOrderInterfaceSerializer>[] = [
  "preoperative_diagnosis_summary",
  "pre_operation_summary",
  "pre_operation_product_summary",
  "position_summary",
  "other_treatment_summary",
  "remark",
];

const dateTimeColumns: Partial<keyof OperatingOrderInterfaceSerializer>[] = [
  "doctor_edit_time",
  "order_time",
  "order_receive_time",
  "order_ack_time",
  "order_perform_time",
  "order_approve_time",
  "created",
  "edited",
  "end_date_time",
];

const columnWidths: WidthsType = {
  id: 70,
  doctor_edit_time: 150,
  order_time: 150,
  order_receive_time: 150,
  order_ack_time: 150,
  order_perform_time: 150,
  order_approve_time: 150,
  created: 150,
  edited: 150,
  end_date_time: 150,
  preoperative_diagnosis_summary: 200,
  pre_operation_summary: 200,
  pre_operation_product_summary: 200,
  position_summary: 200,
  other_treatment_summary: 200,
  remark: 200,
  primary_doctor_name: 200,
  patient_name: 200,
  coverage: 180,
  division_name: 180,
  emr: 400,
  type: 200,
  order_status: 150,
};

const tableHeaders = GenerateTableHeader(columnWidths);
